/* eslint-disable import/extensions */
import * as am5 from '@amcharts/amcharts5';
import * as am5map from '@amcharts/amcharts5/map';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as Collections from 'typescript-collections';
import {type Inventory} from '@unbrs/plastic-data';
import {BrsBaseWidget} from '../base';
import {BrsInventories} from './inventories';

export type BrsPlasticInventoryMapWidgetOptions = {
	dataSource: kendo.data.DataSource;
	geojson?: GeoJSON.FeatureCollection;
};

BrsBaseWidget.registerBrsWidget(BrsInventories);

export class BrsPlasticInventoryMapWidget extends BrsBaseWidget {
	private readonly polygonSeries: am5map.MapPolygonSeries;

	constructor(selector: Element, options?: BrsPlasticInventoryMapWidgetOptions) {
		super(selector, options);

		const dataSource = kendo.data.DataSource.create(this.options.dataSource as kendo.data.DataSourceOptions);
		dataSource.bind('change', this.refresh.bind(this));

		const root = am5.Root.new(this.element[0]!);
		root.setThemes([am5themes_Animated.new(root)]);

		// Create the map chart
		const chart = root.container.children.push(
			am5map.MapChart.new(root, {
				projection: am5map.geoEqualEarth(),
				panX: 'rotateX',
				panY: 'translateY',
				layout: root.horizontalLayout,
			}),
		);
		const zoomControl = chart.set(
			'zoomControl',
			am5map.ZoomControl.new(root, {
				x: am5.percent(7),
				y: am5.percent(25),
			}),
		);
		zoomControl.homeButton.set('visible', true);

		const polygonSeries = chart.series.push(
			am5map.MapPolygonSeries.new(root, {
				geoJSON: options?.geojson,
				valueField: 'countInventoriesPerCountry',
				calculateAggregates: true,
				exclude: ['AQ'],
			}),
		);

		// Set up heat rules
		polygonSeries.set('heatRules', [
			{
				target: polygonSeries.mapPolygons.template,
				dataField: 'value',
				min: am5.color(0x00_00_77),
				max: am5.color(0x00_00_77),
				key: 'fill',
			},
		]);

		polygonSeries.set('reverseGeodata', true);
		polygonSeries.mapPolygons.template.set('tooltipPosition', 'pointer');
		polygonSeries.mapPolygons.template.events.on(
			'pointerover',
			() => {}, // eslint-disable-line @typescript-eslint/no-empty-function
		);
		// ========================================================================
		// BEGIN Disclaimer
		// ========================================================================
		const disclamerWindow = $('#brs-inventory-disclaimer-popup').kendoWindow({
			title: 'Disclaimer',
			width: '500px',
			height: '200px',
			visible: false, // Initially hidden
		}).data('kendoWindow')!;

		const button = am5.Button.new(root, {
			x: am5.percent(6),
			y: am5.percent(94),
			label: am5.Label.new(root, {
				text: 'Disclaimer',
				fill: am5.color(0x00_00_00),
				fillOpacity: 0.5,
				textDecoration: 'underline',
			}),
		});

		button.get('background')?.setAll({
			fillOpacity: 0,
			strokeOpacity: 0,
		});

		button.events.on('pointerover', event => {
			const rect = chart.root.dom.getBoundingClientRect();
			if (disclamerWindow.wrapper) {
				disclamerWindow.wrapper.css({
					top: rect.top + event.point.y - (disclamerWindow.wrapper?.height() ?? 0),
					left: rect.left + event.point.x,
				});
				disclamerWindow.open();
			}
		});
		button.events.on('pointerout', () => {
			disclamerWindow.close();
		});
		chart.children.push(button);
		// ========================================================================
		// BEGIN Popup
		// ========================================================================
		const popup = $('#brs-inventories-map-popup').kendoWindow({
			width: '600px',
			height: '400px',
			visible: false, // Initially hidden
		}).data('kendoWindow')!;

		const inventoriesWidget = $('#brs-inventories-map-list').kendoBrsInventories({pageable: false}).data('kendoBrsInventories');

		polygonSeries.mapPolygons.template.events.on('click', (event: any) => {
			if (
				!event.target.dataItem?.dataContext.inventories
			) {
				return;
			}

			const dataContext = event.target.dataItem.dataContext; // eslint-disable-line @typescript-eslint/no-unsafe-assignment
			const rect = chart.root.dom.getBoundingClientRect();

			if (popup.wrapper) {
				let winTop: number = rect.top + event.point.y; // eslint-disable-line @typescript-eslint/restrict-plus-operands, @typescript-eslint/no-unsafe-assignment
				let winLeft: number = rect.left + event.point.x; // eslint-disable-line @typescript-eslint/restrict-plus-operands, @typescript-eslint/no-unsafe-assignment
				const popupHeight = popup.wrapper.height();
				const popupWidth = popup.wrapper.width();
				if (popupHeight && popupWidth) {
					if (winTop + popupHeight > rect.bottom) {
						winTop = rect.bottom - popupHeight;
					}

					if (winLeft + popupWidth > rect.right) {
						winLeft = rect.right - popupWidth;
					}

					popup.wrapper.css({
						top: winTop,
						left: winLeft,
					});
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					const numberInventories: number = dataContext.inventories.length;
					inventoriesWidget.setDataSource(event.target.dataItem?.dataContext.inventories as Inventory.InventoryData[]);
					popup.title((dataContext.name as string) + ' — ' + numberInventories + ' inventor' + (numberInventories > 1 ? 'ies' : 'y'));
					popup.open();
				}
			}
		});
		// ========================================================================
		// END popup
		// ========================================================================

		this.polygonSeries = polygonSeries;

		this.setInventoriesCountMap();
	}

	refresh(event: {items: Inventory.InventoryData[]}) {
		const inventoriesPerCountry = new Collections.DefaultDictionary<
		string, Inventory.InventoryData[]>(() => []);

		for (const item of event.items) {
			inventoriesPerCountry.getValue(item.iso2).push(item);
		}

		const newData: any = inventoriesPerCountry.keys().map((key: string) => {
			const inventories = inventoriesPerCountry.getValue(key);
			return {
				id: key,
				countInventoriesPerCountry: inventories.length,
				inventories,
			};
		});
		this.polygonSeries.data.setAll(newData); // eslint-disable-line @typescript-eslint/no-unsafe-argument
		this.polygonSeries.set('reverseGeodata', false);
	}

	setInventoriesCountMap() {
		// Const START_COLOR = am5.color(0x35_50_C8);
		// const END_COLOR = am5.color(0x00_00_77);

		this.polygonSeries.set('valueField', 'countInventoriesPerCountry');
		this.polygonSeries.mapPolygons.template.adapters.add(
			'tooltipText',
			(tooltipText, target) => {
				if (target.dataItem) {
					const value = target.dataItem.get(('value' as any)) as number;
					const result
            = '{name}'
            + (value > 0 ? `: {value} inventor${value > 1 ? 'ies' : 'y'}` : '');
					return result;
				}

				return tooltipText;
			},
		);
	}

	zoomToCountry(iso2: string) {
		const result = this.polygonSeries.getDataItemById(iso2);
		if (result) {
			this.polygonSeries.zoomToDataItem(result);
			// Apply highlight state
			const polygon = result.get('mapPolygon');
			const originalFill = polygon.get('fill');
			polygon.animate({key: 'fill', to: am5.color(0xFF_00_00), duration: 1000}); // Example highlight fill color

			setTimeout(() => {
				polygon.animate({key: 'fill', to: originalFill, duration: 1000}); // Example highlight fill color
			}, 1000);
		}
	}
}

declare global {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface JQuery {
		kendoBrsPlasticInventoryMapWidget(options?: BrsPlasticInventoryMapWidgetOptions): JQuery;
		data(widgetName: 'kendoBrsPlasticInventoryMapWidget'): BrsPlasticInventoryMapWidget;
	}
}

BrsBaseWidget.registerBrsWidget(BrsPlasticInventoryMapWidget, 'BrsPlasticInvenotryMapWidget');
kendo.ui.plugin(BrsPlasticInventoryMapWidget);
